import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { name: 'Home', path: '/' },
    { name: 'Menu', path: '/menu' },
    { name: 'About Us', path: '/about' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact Us', path: '/contact' }
  ];

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const logoVariants = {
    initial: { 
      scale: 0, 
      rotate: -360,
      opacity: 0
    },
    animate: { 
      scale: [0, 1.2, 1], 
      rotate: [0, 180, 0],
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        duration: 2.5,
        times: [0, 0.7, 1]
      }
    }
  };

  const halalTextVariants = {
    initial: { 
      opacity: 0,
      scale: 0,
      y: -20
    },
    animate: { 
      opacity: 1,
      scale: [0, 1.1, 1],
      y: 0,
      transition: {
        delay: 1.2,
        duration: 0.8,
        times: [0, 0.8, 1]
      }
    }
  };

  const checkmarkVariants = {
    initial: { 
      pathLength: 0,
      opacity: 0
    },
    animate: { 
      pathLength: 1,
      opacity: 1,
      transition: {
        delay: 1.8,
        duration: 1,
        ease: "easeInOut"
      }
    }
  };

  const sparkleVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: { 
      scale: [0, 1.5, 0.8, 1.2, 1],
      opacity: [0, 1, 0.8, 1, 0.9],
      transition: {
        delay: 2.5,
        duration: 1.5,
        times: [0, 0.2, 0.4, 0.8, 1]
      }
    }
  };

  const textVariants = {
    initial: { opacity: 0, x: -50 },
    animate: { 
      opacity: 1, 
      x: 0,
      transition: {
        delay: 2.8,
        duration: 0.8,
        type: "spring",
        stiffness: 100
      }
    }
  };

  const pulseVariants = {
    animate: {
      scale: [1, 1.05, 1],
      boxShadow: [
        "0 0 0 0 rgba(249, 115, 22, 0.4)",
        "0 0 0 10px rgba(249, 115, 22, 0)",
        "0 0 0 0 rgba(249, 115, 22, 0)"
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        delay: 3.5
      }
    }
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3">
            <motion.div
              variants={pulseVariants}
              animate="animate"
              className="relative"
            >
              <motion.div
                variants={logoVariants}
                initial="initial"
                animate="animate"
                className="w-12 h-12 bg-gradient-to-br from-secondary-500 via-primary-500 to-accent-500 rounded-full flex items-center justify-center relative overflow-hidden"
              >
                {/* Halal Text */}
                <motion.span 
                  variants={halalTextVariants}
                  initial="initial"
                  animate="animate"
                  className="text-white font-bold text-xs absolute top-1"
                >
                  HALAL
                </motion.span>
                
                {/* Animated Checkmark */}
                <motion.svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  className="absolute"
                  style={{ top: '18px', left: '50%', transform: 'translateX(-50%)' }}
                >
                  <motion.path
                    d="M3 10 L8 15 L17 6"
                    stroke="white"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    variants={checkmarkVariants}
                    initial="initial"
                    animate="animate"
                  />
                </motion.svg>

                {/* Halal Dot */}
                <motion.div
                  variants={sparkleVariants}
                  initial="initial"
                  animate="animate"
                  className="w-3 h-3 bg-white rounded-full absolute bottom-1"
                />

                {/* Sparkle Effects */}
                <motion.div
                  variants={sparkleVariants}
                  initial="initial"
                  animate="animate"
                  className="absolute -top-1 -right-1 w-2 h-2 bg-accent-400 rounded-full"
                  style={{ 
                    boxShadow: '0 0 6px rgba(234, 179, 8, 0.8)',
                    animationDelay: '0.2s'
                  }}
                />
                <motion.div
                  variants={sparkleVariants}
                  initial="initial"
                  animate="animate"
                  className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-secondary-400 rounded-full"
                  style={{ 
                    boxShadow: '0 0 4px rgba(239, 68, 68, 0.8)',
                    animationDelay: '0.4s'
                  }}
                />
              </motion.div>

              {/* Rotating Glow Effect */}
              <motion.div
                className="absolute inset-0 rounded-full"
                animate={{
                  rotate: 360,
                  background: [
                    "conic-gradient(from 0deg, transparent, rgba(249, 115, 22, 0.3), transparent)",
                    "conic-gradient(from 180deg, transparent, rgba(239, 68, 68, 0.3), transparent)",
                    "conic-gradient(from 360deg, transparent, rgba(234, 179, 8, 0.3), transparent)"
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: 3,
                  ease: "linear"
                }}
                style={{ zIndex: -1 }}
              />
            </motion.div>

            <motion.div
              variants={textVariants}
              initial="initial"
              animate="animate"
            >
              <motion.h1 
                className="text-xl font-bold bg-gradient-to-r from-secondary-600 via-primary-600 to-accent-600 bg-clip-text text-transparent"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  delay: 3.5
                }}
                style={{
                  backgroundSize: "200% 200%"
                }}
              >
                Gateshead Halal Food Store
              </motion.h1>
              
              {/* Floating Food Icons */}
              <motion.div
                className="absolute -top-2 -right-8 text-xs"
                animate={{
                  y: [-2, 2, -2],
                  rotate: [0, 5, 0, -5, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: 4
                }}
              >
                🥩
              </motion.div>
              
              <motion.div
                className="absolute -bottom-1 -right-12 text-xs"
                animate={{
                  y: [2, -2, 2],
                  rotate: [0, -3, 0, 3, 0]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  delay: 4.5
                }}
              >
                🌿
              </motion.div>
            </motion.div>
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden md:flex space-x-8">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 3 + index * 0.1, duration: 0.5 }}
              >
                <Link
                  to={item.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 hover:scale-105 ${
                    location.pathname === item.path
                      ? 'text-primary-600 bg-primary-50 shadow-md'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-primary-600 focus:outline-none"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="h-6 w-6" />
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                    location.pathname === item.path
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header;